import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import type { APIRequestLog, LogFilters, Paginated } from '@/types/auditLog';
import { Pager } from './Pager';

interface Props {
  filters: LogFilters;
  onFiltersChange: (f: LogFilters) => void;
  refreshTrigger: number;
}

export function RequestLogTable({ filters, onFiltersChange, refreshTrigger }: Props) {
  const [result, setResult] = useState<Paginated<APIRequestLog> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.requestLogs
      .list(filters)
      .then((r) => {
        setResult(r);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load request logs'));
  }, [filters, refreshTrigger]);

  return (
    <>
      {error && <div className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
      {!result ? (
        <div className="py-8 text-center text-muted-foreground">Loading request logs…</div>
      ) : result.items.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">No requests match these filters.</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Request</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.items.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="text-sm whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</TableCell>
                <TableCell className="text-sm">
                  {log.actor_type === 'api_key' ? log.api_key_name || `#${log.api_key_id}` : <Badge variant="outline">{log.actor_type}</Badge>}
                </TableCell>
                <TableCell className="font-mono text-xs" title={log.request_id}>
                  {log.method} {log.path}
                </TableCell>
                <TableCell>
                  <Badge variant={log.status_code < 400 ? 'default' : 'destructive'}>{log.status_code}</Badge>
                </TableCell>
                <TableCell className="text-sm">{log.duration_ms} ms</TableCell>
                <TableCell className="text-sm font-mono">{log.ip_address}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {result && (
        <Pager page={result.page} totalPages={result.total_pages} total={result.total} onPage={(page) => onFiltersChange({ ...filters, page })} />
      )}
    </>
  );
}
